import { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Alert, Row, Col } from "react-bootstrap";
import Api from "../../../Services/Api";

interface TimeIntervalUnit {
  id: number;
  name: string;
  hoursDuration: number;
  createdAt?: string;
  updatedAt?: string;
}

const TimeIntervalUnits = () => {
  const [units, setUnits] = useState<TimeIntervalUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState<TimeIntervalUnit | null>(null);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  
  const [formData, setFormData] = useState({
    name: "",
    hoursDuration: 0,
  });

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = async () => {
    setLoading(true);
    const api = new Api();
    const data = await api.getTimeIntervalUnits();
    if (data) {
      setUnits(data);
    }
    setLoading(false);
  };

  const handleOpenCreate = () => {
    setEditingUnit(null);
    setFormData({ name: "", hoursDuration: 0 });
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const handleOpenEdit = (unit: TimeIntervalUnit) => {
    setEditingUnit(unit);
    setFormData({
      name: unit.name,
      hoursDuration: unit.hoursDuration,
    });
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingUnit(null);
    setFormData({ name: "", hoursDuration: 0 });
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.name || formData.hoursDuration <= 0) {
      setError("Please fill in all fields with valid values");
      return;
    }

    try {
      const api = new Api();
      if (editingUnit) {
        await api.updateTimeIntervalUnit(editingUnit.id, formData);
        setSuccess("Time interval unit updated successfully");
      } else {
        await api.createTimeIntervalUnit(formData);
        setSuccess("Time interval unit created successfully");
      }
      handleClose();
      loadUnits();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this time interval unit?")) {
      return;
    }

    try {
      const api = new Api();
      await api.deleteTimeIntervalUnit(id);
      setSuccess("Time interval unit deleted successfully");
      loadUnits();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h3>Time Interval Units</h3>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleOpenCreate}>
            Create New
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Hours Duration</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {units.map((unit) => (
            <tr key={unit.id}>
              <td>{unit.id}</td>
              <td>{unit.name}</td>
              <td>{unit.hoursDuration}</td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleOpenEdit(unit)}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(unit.id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUnit ? "Edit Time Interval Unit" : "Create Time Interval Unit"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Hours Duration</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.hoursDuration}
                onChange={(e) => setFormData({ ...formData, hoursDuration: parseInt(e.target.value) || 0 })}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingUnit ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default TimeIntervalUnits;
